import { Button, Card, Collapse, Divider, message, Popconfirm } from 'antd';
import React, { FC, useCallback, useState } from 'react';
import { FlexibleImageAvatar } from '../flexible-image-avatar/FlexibleImageAvatar';
import CollapsePanel from 'antd/lib/collapse/CollapsePanel';
import {
	checkHasApprovalHierarchy,
	compareStrings,
	getCurrency,
	nullSafeGet,
	nullSafeGetOrElse,
	recordHasContractorId,
} from '../../utils/DataAccessUtils';
import ApprovalHierarchyDisplayRFPAward from '../approval_hierarchy_display_rfp/ApprovalHierarchyDisplayRFPAward';
import { connect } from 'react-redux';
import { AWARD_STATUSES } from '../../constants/BidderStatuses';
import { CheckOutlined } from '@ant-design/icons';
import { currencyFormatter } from '../../utils/DataFormatterUtils';
import FileUploader from '../file_uploader/FileUploader';
import { withRouter } from 'react-router';
import { withUserType } from '../withUserType';
import { SupplierFacilityName } from '../name_component/supplierFacility/supplierFacilityNameComponent';
import { UserName } from '../name_component/user/UserNameComponent';
import FormDots from '../common/FormDots';
import FormDashes from '../FormDashes';
import { CustomCurrencyDisplay } from '../custom_currency_display/CustomCurrencyDisplay';
import DateTimeHover from '../date_time_component/DateTime';
import { userTimeZone } from '../../utils/DataConstants';

const BidderCard: FC<any> = ({
	userType,
	bidder,
	bidderAwarded,
	requestForProposal,
	awardUpdating,
	handleAwardStatusChange,
	lowestBidderSupplierFacilityId,
	size = 'default',
	showSupplierInfo = true,
	updateQuote,
	currentUser,
	companyConfig,
}) => {
	const width = size === 'large' ? '100%' : '320px';
	const currency = getCurrency({ supplierFacility: nullSafeGet('supplierFacility', bidder) });
	const supplierName = nullSafeGetOrElse(
		'supplierFacility.name',
		bidder,
		nullSafeGetOrElse('supplierFacility.displayName', bidder, 'this supplier')
	);
	const awardConfirmTitle = (
		<div style={{ maxWidth: 355, whiteSpace: 'normal' }}>
			{`${supplierName} will be awarded this RFP and notified. All other proposals will be declined.`}
		</div>
	);
	return (
		<Card
			key={bidder.supplierFacilityId}
			className="bidComparisonTable"
			style={{ minWidth: '320px', width }}
			bodyStyle={{ padding: 0 }}
		>
			{showSupplierInfo ? (
				<div>
					<div style={{ padding: 16 }}>
						<SupplierFacilityName
							facility={nullSafeGet('supplierFacility', bidder)}
							mode={'card'}
							placement={'top'}
							allowHover={false}
							size={'large'}
							subTitle={null}
						/>
					</div>
					<Divider
						style={{
							marginTop: 8,
							marginBottom: 0,
							marginLeft: 0,
							marginRight: 0,
							borderTopColor: `#F0F0F0`,
						}}
					/>
				</div>
			) : null}

			<div className="">
				<div className="">
					<Collapse ghost>
						<CollapsePanel header="Scope" key="1">
							<div
								className="mb-4 flex flex-col overflow-auto"
								dangerouslySetInnerHTML={{
									__html: nullSafeGetOrElse('proposal.scope', bidder, '--'),
								}}
							/>
						</CollapsePanel>
						<Divider
							style={{
								marginTop: 0,
								marginBottom: 0,
								marginLeft: 12,
								marginRight: 12,
								borderTopColor: `#F0F0F0`,
							}}
						/>
					</Collapse>
					<Collapse ghost>
						<CollapsePanel
							header={`Attachments ${
								nullSafeGet('proposal.attachments.length', bidder) > 0
									? `(${nullSafeGet('proposal.attachments.length', bidder)})`
									: ''
							}`}
							key="1"
							collapsible={nullSafeGet('proposal.attachments.0', bidder) ? 'header' : 'disabled'}
						>
							<FileUploader
								roleType={userType}
								defaultFileList={nullSafeGetOrElse('proposal.attachments', bidder, [])}
								uploaderType={'readOnly'}
							/>
						</CollapsePanel>
					</Collapse>
					<div className="sectionTextWrapperAroundButtons">
						<Divider style={{ marginTop: 0, marginBottom: 0, borderTopColor: `#F0F0F0` }} />
						<div
							className="sectionTextWrapperWithBid"
							style={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'flex-start',
								// Float everything to the bottom so the amount + action row sit at a constant
								// position across all bidder cards (no mid gap), keeping comparison easy.
								justifyContent: 'flex-end',
								// Constant height so the bid summary + action row line up across all bidder cards.
								minHeight: 100,
								gap: 8,
							}}
						>
							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'flex-start',
									// A bit more room between the bid total and the approval/action buttons below it.
									gap: 12,
									width: '100%',
								}}
							>
								<div
									style={{
										display: 'flex',
										flexDirection: 'row',
										alignItems: 'flex-end',
										justifyContent: 'space-between',
										width: '100%',
										gap: 12,
									}}
								>
									<h5 style={{ marginBottom: 0 }}>
										<CustomCurrencyDisplay
											value={nullSafeGet('proposal.totalAfterTax', bidder)}
											currency={currency}
											currencyStyle={{ fontSize: 16 }}
										/>
									</h5>
									<div
										style={{
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'flex-end',
											textAlign: 'right',
										}}
									>
										{bidder.supplierFacilityId === lowestBidderSupplierFacilityId &&
										!bidderAwarded &&
										!compareStrings(nullSafeGet('awardStatus', bidder), AWARD_STATUSES.Awarded) &&
										!compareStrings(nullSafeGet('awardStatus', bidder), AWARD_STATUSES.Declined) ? (
											<div className="bidSummary_lowBidder">
												<span>Apparent Low Bidder</span>
											</div>
										) : null}
										{compareStrings(
											nullSafeGet('awardStatus', bidder),
											AWARD_STATUSES.AssignedSupplier
										) ? (
											<div className="bidSummary_assignedSupplier">Assigned Supplier</div>
										) : null}
									</div>
								</div>
								{checkHasApprovalHierarchy(nullSafeGet('proposal', bidder)) ? (
									<ApprovalHierarchyDisplayRFPAward
										requestForProposal={requestForProposal}
										bidder={bidder}
										bidderAwarded={bidderAwarded}
										awardUpdating={awardUpdating}
										handleAwardStatusChange={handleAwardStatusChange}
										currentUser={currentUser}
										companyConfig={companyConfig}
										userType={userType}
									/>
								) : compareStrings(nullSafeGet('awardStatus', bidder), AWARD_STATUSES.Awarded) ? (
									<div style={{ fontSize: 16 }}>Awarded</div>
								) : compareStrings(nullSafeGet('awardStatus', bidder), AWARD_STATUSES.Declined) ? (
									<div style={{ fontSize: 16 }}>Declined</div>
								) : bidderAwarded ? (
									<Popconfirm
										onConfirm={() =>
											handleAwardStatusChange(
												requestForProposal.id,
												bidder.supplierFacilityId,
												'declined'
											)
										}
										placement="topLeft"
										title="Send declined note to the supplier?"
										okText="Confirm decline"
										cancelText="Cancel"
									>
										<Button loading={awardUpdating} type="primary">
											Decline
										</Button>
									</Popconfirm>
								) : (
									<div className="flex flex-row items-center gap-2">
										<Popconfirm
											onConfirm={() =>
												handleAwardStatusChange(
													requestForProposal.id,
													bidder.supplierFacilityId,
													'awarded'
												)
											}
											placement="topLeft"
											title={awardConfirmTitle}
											okText="Confirm award"
											cancelText="Cancel"
										>
											<Button loading={awardUpdating} type="primary">
												Award
											</Button>
										</Popconfirm>
										<Popconfirm
											onConfirm={() =>
												handleAwardStatusChange(
													requestForProposal.id,
													bidder.supplierFacilityId,
													'declined'
												)
											}
											placement="topLeft"
											title="Are you sure, you want to decline?"
											okText="Confirm decline"
											cancelText="Cancel"
										>
											<Button
												loading={awardUpdating}
												type="ghost"
												style={{ backgroundColor: 'white' }}
											>
												Decline
											</Button>
										</Popconfirm>
									</div>
								)}
							</div>
						</div>
						<Divider style={{ marginTop: 0, marginBottom: 4 }} />
					</div>
				</div>
			</div>
			{[
				{ key: 'Incurred Cost', value: 'incurredCostTotalBeforeTax' },
				{ key: 'Labor', value: 'laborTotalBeforeTax' },
				{ key: 'Materials', value: 'materialTotalBeforeTax' },
				{ key: 'Travel', value: 'travelTotalBeforeTax' },
				{ key: 'Freight', value: 'freightTotalBeforeTax' },
				{ key: 'Miscellaneous', value: 'miscTotalBeforeTax' },
				{ key: 'Total Before Tax', value: 'totalBeforeTax' },
				{ key: 'Tax', value: 'tax' },
				{ key: 'Grand Total', value: 'totalAfterTax' },
			].map((category) => (
				<div className="py-3" key={category.key}>
					<div>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								margin: '0 16px',
								fontSize: 13,
								justifyContent: 'space-between',
							}}
						>
							<div className="sectionTextMain font-semibold">{category.key}</div>
							<FormDashes style={{ color: '#D9D9D9' }} />
							<div className="sectionTextSecondary">
								<CustomCurrencyDisplay
									value={nullSafeGet(`proposal.${category.value}`, bidder)}
									currency={currency}
								/>
							</div>
						</div>
					</div>
				</div>
			))}
			<div className={'mb-3 mt-4 flex justify-center'}>
				<span style={{ fontSize: 11 }}>
					Updated by{' '}
					<UserName
						mode={'inline'}
						contact={nullSafeGet('createdByContact', requestForProposal)}
						position={'bottom'}
					/>{' '}
					<DateTimeHover
						timestamp={nullSafeGet('createdAt', requestForProposal)}
						timeZone={userTimeZone}
					/>
				</span>
			</div>
		</Card>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
});
const mapDispatchToProps = (dispatch, ownProps) => ({});

export default withRouter<any, any>(
	withUserType(connect(mapStateToProps, mapDispatchToProps)(BidderCard))
);
